import { Form } from '@components/ui/Form';
import { useAcceptInvite } from '@hooks/useAcceptInvite';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import Link from '@components/ui/Link';
import { Input } from '@components/ui/Input';
import {
    LoginRoot,
    LoginWelcome,
    LoginSubtitle,
    LoginFormWrapper,
    LoginSubmitButton,
    AuthHiddenAutofillTrap,
} from '../style';

const AcceptInvite = () => {
    const { form, loading, onFinish, hasValidParams } = useAcceptInvite();

    if (!hasValidParams) {
        return (
            <LoginRoot>
                <LoginWelcome>Invalid invite link</LoginWelcome>
                <LoginSubtitle>
                    This link is missing required parameters. Please use the link from your invitation email, or{' '}
                    <Link to="/sign-in">sign in</Link> if you already have an account.
                </LoginSubtitle>
            </LoginRoot>
        );
    }

    return (
        <LoginRoot>
            <LoginWelcome>Accept invitation</LoginWelcome>
            <LoginSubtitle>
                Set your name and password to complete your account.
            </LoginSubtitle>

            <LoginFormWrapper>
                <Form
                    form={form}
                    onFinish={onFinish}
                    layout="vertical"
                    requiredMark={false}
                    autoComplete="off"
                    name="accept_invite_form"
                    id="accept-invite-form"
                >
                    <AuthHiddenAutofillTrap aria-hidden="true">
                        <input type="text" name="prevent_autofill_username" tabIndex={-1} autoComplete="off" readOnly />
                        <input
                            type="password"
                            name="prevent_autofill_password"
                            tabIndex={-1}
                            autoComplete="off"
                            readOnly
                        />
                    </AuthHiddenAutofillTrap>
                    <Form.Item name="name" rules={[{ required: true, message: 'Please enter your name' }]}>
                        <Input
                            noAutofill
                            $maxWidth="100%"
                            $background="transparent"
                            $size="0.9375rem"
                            $padding="12px 14px"
                            prefix={<UserOutlined />}
                            placeholder="Full name"
                        />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        rules={[
                            { required: true, message: 'Please enter your password' },
                            {
                                validator: (_, value) =>
                                    !value || value.length >= 6
                                        ? Promise.resolve()
                                        : Promise.reject(new Error('At least 6 characters')),
                            },
                        ]}
                    >
                        <Input
                            noAutofill
                            isPassword
                            $maxWidth="100%"
                            $background="transparent"
                            $size="0.9375rem"
                            $padding="12px 14px"
                            prefix={<LockOutlined />}
                            placeholder="Password"
                        />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        dependencies={['password']}
                        rules={[
                            { required: true, message: 'Please confirm your password' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) return Promise.resolve();
                                    return Promise.reject(new Error("Passwords don't match"));
                                },
                            }),
                        ]}
                    >
                        <Input
                            noAutofill
                            isPassword
                            $maxWidth="100%"
                            $background="transparent"
                            $size="0.9375rem"
                            $padding="12px 14px"
                            prefix={<LockOutlined />}
                            placeholder="Confirm password"
                        />
                    </Form.Item>
                    <Form.Item>
                        <LoginSubmitButton type="primary" htmlType="submit" block loading={loading} size="large">
                            Sign up
                        </LoginSubmitButton>
                    </Form.Item>
                </Form>
            </LoginFormWrapper>
        </LoginRoot>
    );
};

export default AcceptInvite;
